import { Card, CardActions } from "@progress/kendo-react-layout";
import { ExternalDropZone, Upload, UploadFileInfo, UploadOnStatusChangeEvent } from "@progress/kendo-react-upload";
import { useFastaStore } from "@presentation/stores/FastaStore";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useInjection } from "@presentation/components/dependencyInjection"; //if you want to break the whole project with an error just at runtime(builds normally) impossible to guess just remove the /dependencyInjection
import React from "react";
import { useNotifications } from "@presentation/components/notifications";
import { UploadSettingsService } from "@application/useCases/softwareSettings/UploadSettingsService";
import { SoftwareSettingsMetadata, SettingsTypeEnumSchema } from "@domain/schemas/BI";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";

/**
 * Define the interface for the event parameter in onStatusChange
 */
interface StatusChangeEvent {
  status: number;
  data?: { message: string };
  response?: { response: { data: { message: string } } };
}

interface UploadSettingsProps {
  onUploadSuccess?: (uploadedSettings: SoftwareSettingsMetadata) => void;
  settingsType: SettingsTypeEnumSchema;
}

const uploadRef = React.createRef<Upload>();

// UploadCard component with TypeScript
const UploadSettings: React.FC<UploadSettingsProps> = ({ onUploadSuccess, settingsType }) => {
  const uploadSettingsService = useInjection(UploadSettingsService);
  const { pushNotification } = useNotifications();
  const softwareVersion = useJobSetupStore((state) => state.CreateJobState.softwareVersion);

  const uploadVisibilitiy = useFastaStore((state) => state.isUploadVisible);
  const setUploadVisibilitiy = useFastaStore((state) => state.setUploadVisibilitiy);
  const uploadResults = useFastaStore((state) => state.uploadResults);
  const setUploadResults = useFastaStore((state) => state.setUploadResults);

  const onSaveUrl = async (
    files: UploadFileInfo[],
    _options: { formData: FormData; requestOptions: unknown },
    onProgress: (uid: string, event: ProgressEvent<EventTarget>) => void,
  ): Promise<{ uid: string }> => {
    if (files.length > 0 && files[0].getRawFile) {
      const file = files[0].getRawFile();
      if (file) {
        if (!softwareVersion) {
          pushNotification("Select a Spectronaut version before uploading settings.", "warning", 10000);
          return { uid: "" };
        }

        // Call the upload service and pass the onProgress callback
        await uploadSettingsService
          .uploadSettingsAsync(
            file,
            softwareVersion,
            (progressValue) => {
              // Update the progress as the file uploads
              onProgress(files[0].uid, {
                lengthComputable: true,
                loaded: progressValue,
                total: 100,
              } as ProgressEvent<EventTarget>);
            },
            settingsType,
          )
          .then((response) => {
            // Once the upload is complete
            if (onUploadSuccess) {
              pushNotification("The settings file was successfuly uploaded.", "success", 10000);
              onUploadSuccess(response as unknown as SoftwareSettingsMetadata);
            }
            return { uid: response.id };
          })
          .catch((error) => {
            pushNotification(error.response?.data?.message ? error.response.data.message : "An error occurred while uploading the file.", "error", 10000);
          });
      } else {
        console.log(new Error("File could not be retrieved from UploadFileInfo."));
      }
    } else {
      console.log(new Error("No files to upload."));
    }
    return { uid: "" };
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    const statusEvent = event as unknown as StatusChangeEvent;

    if (statusEvent.status === 200 && statusEvent.data) {
      setUploadResults(statusEvent.data.message);
      setUploadVisibilitiy(true);
    } else if (statusEvent.response) {
      setUploadResults(statusEvent.response.response.data.message);
      alert(uploadResults);
    }
  };

  const toggleDialog = () => {
    setUploadVisibilitiy(false);
    window.location.href = "/";
  };

  return (
    <div>
      {uploadVisibilitiy && (
        <Dialog title="Upload Results" onClose={toggleDialog} width={"100%"} height={"100%"}>
          <p>Results of upload:</p>
          <p>{uploadResults}</p>

          <DialogActionsBar>
            <Button onClick={toggleDialog}>OK</Button>
          </DialogActionsBar>
        </Dialog>
      )}
      <Card style={{ height: "100%" }}>
        <CardActions>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              fontSize: "14px",
              gridTemplateRows: "auto auto auto",
            }}
          >
            <br></br>
            <div>
              <ExternalDropZone style={{ minWidth: "35vh" }} uploadRef={uploadRef} />
              <Upload
                ref={uploadRef}
                batch={false}
                multiple={false}
                withCredentials={false} //somehow cors doesnt work if set to true
                defaultFiles={[]}
                // restrictions={{
                //   allowedExtensions: [".prop"],
                //   maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
                // }}
                saveUrl={onSaveUrl}
                onStatusChange={(event) => onStatusChange(event)}
                saveField="files"
              />
            </div>
          </div>
        </CardActions>
      </Card>
    </div>
  );
};

export default UploadSettings;
